import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Table,
  Statistic,
  Tag,
  Typography,
  DatePicker,
  Button,
  Spin,
  message,
  Tabs,
  Space,
  Progress
} from 'antd';
import {
  ClockCircleOutlined,
  DollarOutlined,
  ReloadOutlined,
  DownloadOutlined,
  PrinterOutlined,
  WarningOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';
import vendorBillService, {
  type AgingReportData,
  type AgingReportByVendor,
  type AgingReportDetail
} from '@/services/vendorBillService';

const { Title, Text } = Typography;

const APAgingReport: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<AgingReportData | null>(null);
  const [asOfDate, setAsOfDate] = useState<dayjs.Dayjs>(dayjs());

  const fetchData = async () => {
    setLoading(true);
    try {
      const result = await vendorBillService.getAgingReport(asOfDate.format('YYYY-MM-DD'));
      setData(result);
    } catch (error: any) {
      message.error(error.message || 'Failed to fetch aging report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const getAgingBucketColor = (bucket: string) => {
    switch (bucket) {
      case 'current': return 'green';
      case 'days30': return 'blue';
      case 'days60': return 'orange';
      case 'days90Plus': return 'red';
      default: return 'default';
    }
  };

  const getAgingBucketLabel = (bucket: string) => {
    switch (bucket) {
      case 'current': return '0-30 Days';
      case 'days30': return '31-60 Days';
      case 'days60': return '61-90 Days';
      case 'days90Plus': return '90+ Days';
      default: return bucket;
    }
  };

  const handleExport = () => {
    if (!data || data.details.length === 0) {
      message.warning('No data to export');
      return;
    }
    const wb = XLSX.utils.book_new();
    // Sheet 1: By Vendor
    const vendorRows = data.byVendor.map(v => ({
      'Vendor': v.vendorName || '',
      'Code': v.vendorCode || '',
      'Current (0-30)': Number(v.current || 0),
      '31-60 Days': Number(v.days30 || 0),
      '61-90 Days': Number(v.days60 || 0),
      '90+ Days': Number(v.days90Plus || 0),
      'Total': Number(v.total || 0),
    }));
    const ws1 = XLSX.utils.json_to_sheet(vendorRows);
    ws1['!cols'] = [{ wch: 30 }, { wch: 12 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 14 }];
    XLSX.utils.book_append_sheet(wb, ws1, 'By Vendor');
    // Sheet 2: Details
    const detRows = data.details.map(d => ({
      'Bill No': d.billNo || '',
      'Vendor': d.vendorName || '',
      'Bill Date': d.billDate ? dayjs(d.billDate).format('YYYY-MM-DD') : '',
      'Due Date': d.dueDate ? dayjs(d.dueDate).format('YYYY-MM-DD') : '',
      'Amount': Number(d.amountTotal || 0),
      'Balance': Number(d.amountResidual || 0),
      'Days Overdue': d.daysOverdue || 0,
      'Aging Bucket': getAgingBucketLabel(d.agingBucket),
    }));
    const ws2 = XLSX.utils.json_to_sheet(detRows);
    ws2['!cols'] = [{ wch: 14 }, { wch: 30 }, { wch: 12 }, { wch: 12 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 14 }];
    XLSX.utils.book_append_sheet(wb, ws2, 'Details');
    XLSX.writeFile(wb, `AP_Aging_Report_${dayjs().format('YYYYMMDD_HHmmss')}.xlsx`);
    message.success('AP Aging Report exported');
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const vendorColumns: ColumnsType<AgingReportByVendor> = [
    {
      title: 'Vendor',
      key: 'vendor',
      render: (_, record) => (
        <div>
          <Text strong>{record.vendorName}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: 12 }}>{record.vendorCode}</Text>
        </div>
      )
    },
    {
      title: 'Current (0-30)',
      dataIndex: 'current',
      key: 'current',
      align: 'right',
      render: (value) => (
        <Text style={{ color: value > 0 ? '#52c41a' : undefined }}>
          {formatCurrency(value)}
        </Text>
      )
    },
    {
      title: '31-60 Days',
      dataIndex: 'days30',
      key: 'days30',
      align: 'right',
      render: (value) => (
        <Text style={{ color: value > 0 ? '#1890ff' : undefined }}>
          {formatCurrency(value)}
        </Text>
      )
    },
    {
      title: '61-90 Days',
      dataIndex: 'days60',
      key: 'days60',
      align: 'right',
      render: (value) => (
        <Text style={{ color: value > 0 ? '#faad14' : undefined }}>
          {formatCurrency(value)}
        </Text>
      )
    },
    {
      title: '90+ Days',
      dataIndex: 'days90Plus',
      key: 'days90Plus',
      align: 'right',
      render: (value) => (
        <Text style={{ color: value > 0 ? '#ff4d4f' : undefined }}>
          {formatCurrency(value)}
        </Text>
      )
    },
    {
      title: 'Total',
      dataIndex: 'total',
      key: 'total',
      align: 'right',
      render: (value) => <Text strong>{formatCurrency(value)}</Text>,
      sorter: (a, b) => a.total - b.total,
      defaultSortOrder: 'descend'
    }
  ];

  const detailColumns: ColumnsType<AgingReportDetail> = [
    {
      title: 'Bill No',
      dataIndex: 'billNo',
      key: 'billNo',
      render: (value) => <Text strong>{value}</Text>
    },
    {
      title: 'Vendor',
      dataIndex: 'vendorName',
      key: 'vendorName'
    },
    {
      title: 'Bill Date',
      dataIndex: 'billDate',
      key: 'billDate',
      render: (value) => dayjs(value).format('YYYY-MM-DD')
    },
    {
      title: 'Due Date',
      dataIndex: 'dueDate',
      key: 'dueDate',
      render: (value) => value ? dayjs(value).format('YYYY-MM-DD') : '-'
    },
    {
      title: 'Amount',
      dataIndex: 'amountTotal',
      key: 'amountTotal',
      align: 'right',
      render: (value) => formatCurrency(value)
    },
    {
      title: 'Balance Due',
      dataIndex: 'amountResidual',
      key: 'amountResidual',
      align: 'right',
      render: (value) => <Text strong>{formatCurrency(value)}</Text>
    },
    {
      title: 'Days Overdue',
      dataIndex: 'daysOverdue',
      key: 'daysOverdue',
      align: 'center',
      render: (value) => (
        <Text style={{ color: value > 60 ? '#ff4d4f' : value > 30 ? '#faad14' : '#52c41a' }}>
          {value}
        </Text>
      ),
      sorter: (a, b) => a.daysOverdue - b.daysOverdue
    },
    {
      title: 'Aging',
      dataIndex: 'agingBucket',
      key: 'agingBucket',
      render: (bucket) => (
        <Tag color={getAgingBucketColor(bucket)}>
          {getAgingBucketLabel(bucket)}
        </Tag>
      )
    }
  ];

  const calculatePercentage = (value: number, total: number) => {
    if (total === 0) return 0;
    return Math.round((value / total) * 100);
  };

  return (
    <Spin spinning={loading}>
      <div>
        {/* Header */}
        <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
          <Col>
            <Title level={4} style={{ margin: 0 }}>
              <ClockCircleOutlined style={{ marginRight: 8 }} />
              AP Aging Report
            </Title>
            <Text type="secondary">
              Accounts Payable aging analysis as of {asOfDate.format('MMMM D, YYYY')}
            </Text>
          </Col>
          <Col>
            <Space>
              <DatePicker
                value={asOfDate}
                onChange={(date) => date && setAsOfDate(date)}
                allowClear={false}
              />
              <Button icon={<ReloadOutlined />} onClick={fetchData}>
                Refresh
              </Button>
              <Button icon={<PrinterOutlined />}>Print</Button>
              <Button icon={<DownloadOutlined />} onClick={handleExport}>Export</Button>
            </Space>
          </Col>
        </Row>

        {/* Summary Cards */}
        {data && (
          <>
            <Row gutter={16} style={{ marginBottom: 24 }}>
              <Col xs={24} sm={12} md={8} lg={4}>
                <Card size="small">
                  <Statistic
                    title="Current (0-30)"
                    value={data.summary.current}
                    prefix={<DollarOutlined />}
                    precision={2}
                    valueStyle={{ color: '#52c41a', fontSize: 18 }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} md={8} lg={4}>
                <Card size="small">
                  <Statistic
                    title="31-60 Days"
                    value={data.summary.days30}
                    prefix={<DollarOutlined />}
                    precision={2}
                    valueStyle={{ color: '#1890ff', fontSize: 18 }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} md={8} lg={4}>
                <Card size="small">
                  <Statistic
                    title="61-90 Days"
                    value={data.summary.days60}
                    prefix={<DollarOutlined />}
                    precision={2}
                    valueStyle={{ color: '#faad14', fontSize: 18 }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} md={8} lg={4}>
                <Card size="small">
                  <Statistic
                    title={<><WarningOutlined style={{ color: '#ff4d4f' }} /> 90+ Days</>}
                    value={data.summary.days90Plus}
                    prefix={<DollarOutlined />}
                    precision={2}
                    valueStyle={{ color: '#ff4d4f', fontSize: 18 }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} md={8} lg={8}>
                <Card size="small">
                  <Statistic
                    title="Total Outstanding"
                    value={data.summary.total}
                    prefix={<DollarOutlined />}
                    precision={2}
                    valueStyle={{ fontSize: 18 }}
                  />
                </Card>
              </Col>
            </Row>

            {/* Aging Distribution */}
            <Card title="Aging Distribution" style={{ marginBottom: 24 }} size="small">
              <Row gutter={16}>
                <Col span={6}>
                  <div style={{ marginBottom: 8 }}>
                    <Text>Current (0-30 days)</Text>
                  </div>
                  <Progress
                    percent={calculatePercentage(data.summary.current, data.summary.total)}
                    strokeColor="#52c41a"
                    format={(p) => `${p}%`}
                  />
                </Col>
                <Col span={6}>
                  <div style={{ marginBottom: 8 }}>
                    <Text>31-60 days</Text>
                  </div>
                  <Progress
                    percent={calculatePercentage(data.summary.days30, data.summary.total)}
                    strokeColor="#1890ff"
                    format={(p) => `${p}%`}
                  />
                </Col>
                <Col span={6}>
                  <div style={{ marginBottom: 8 }}>
                    <Text>61-90 days</Text>
                  </div>
                  <Progress
                    percent={calculatePercentage(data.summary.days60, data.summary.total)}
                    strokeColor="#faad14"
                    format={(p) => `${p}%`}
                  />
                </Col>
                <Col span={6}>
                  <div style={{ marginBottom: 8 }}>
                    <Text>90+ days</Text>
                  </div>
                  <Progress
                    percent={calculatePercentage(data.summary.days90Plus, data.summary.total)}
                    strokeColor="#ff4d4f"
                    format={(p) => `${p}%`}
                  />
                </Col>
              </Row>
            </Card>

            {/* Data Tables */}
            <Tabs defaultActiveKey="byVendor">
              <Tabs.TabPane tab="By Vendor" key="byVendor">
                <Card size="small">
                  <Table
                    dataSource={data.byVendor}
                    columns={vendorColumns}
                    rowKey="vendorId"
                    pagination={{ defaultPageSize: 10, showSizeChanger: true }}
                    size="small"
                    summary={() => (
                      <Table.Summary fixed>
                        <Table.Summary.Row>
                          <Table.Summary.Cell index={0}>
                            <Text strong>Total</Text>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={1} align="right">
                            <Text strong>{formatCurrency(data.summary.current)}</Text>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={2} align="right">
                            <Text strong>{formatCurrency(data.summary.days30)}</Text>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={3} align="right">
                            <Text strong>{formatCurrency(data.summary.days60)}</Text>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={4} align="right">
                            <Text strong>{formatCurrency(data.summary.days90Plus)}</Text>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={5} align="right">
                            <Text strong style={{ color: '#1890ff' }}>
                              {formatCurrency(data.summary.total)}
                            </Text>
                          </Table.Summary.Cell>
                        </Table.Summary.Row>
                      </Table.Summary>
                    )}
                  />
                </Card>
              </Tabs.TabPane>
              <Tabs.TabPane tab="Detailed Bills" key="details">
                <Card size="small">
                  <Table
                    dataSource={data.details}
                    columns={detailColumns}
                    rowKey="billId"
                    pagination={{ defaultPageSize: 15, showSizeChanger: true }}
                    size="small"
                  />
                </Card>
              </Tabs.TabPane>
            </Tabs>
          </>
        )}

        {!data && !loading && (
          <Card>
            <div style={{ textAlign: 'center', padding: 40 }}>
              <ClockCircleOutlined style={{ fontSize: 48, color: '#ccc' }} />
              <div style={{ marginTop: 16 }}>
                <Text type="secondary">No aging data available</Text>
              </div>
            </div>
          </Card>
        )}
      </div>
    </Spin>
  );
};

export default APAgingReport;